import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, UserCog, Plus } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

export default function ParentsDirectory() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");
  const [flag, setFlag] = useState("all");
  const [open, setOpen] = useState(false);
  const [famOptions, setFamOptions] = useState([]);
  const load = () => api.get("/people/parents").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  const create = async (v) => { try { await api.post("/people/parents", v); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  useEffect(() => {
    load();
    api.get("/people/families").then((r) => setFamOptions(r.data.map((f) => ({ value: f.id, label: f.name })))).catch(() => {});
  }, []);
  const families = [...new Set(rows.map((r) => r.family_name).filter(Boolean))];
  const filtered = rows.filter((p) =>
    (!q || (p.name || "").toLowerCase().includes(q.toLowerCase()) || (p.email || "").toLowerCase().includes(q.toLowerCase())) &&
    (family === "all" || p.family_name === family) &&
    (flag === "all" || (flag === "primary" && p.is_primary) || (flag === "payer" && p.is_payer) ||
      (flag === "active" && p.account_status === "active") || (flag === "suspended" && p.account_status === "suspended")));
  return (
    <div data-testid="parents-directory">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("parentFiles")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button className="gap-2" data-testid="add-parent-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />{t("addParent")}</Button>
          <Select value={family} onValueChange={setFamily}>
            <SelectTrigger className="w-44" data-testid="parent-filter-family"><SelectValue placeholder={t("allFamilies")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allFamilies")}</SelectItem>{families.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={flag} onValueChange={setFlag}>
            <SelectTrigger className="w-44" data-testid="parent-filter-flag"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              <SelectItem value="primary">{t("is_primary")}</SelectItem>
              <SelectItem value="payer">{t("is_payer")}</SelectItem>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="suspended">suspended</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="parent-search" className="pl-9 w-56" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </div>
      <EntityDialog open={open} onOpenChange={setOpen} title={t("addParent")} submitLabel={t("create")} onSubmit={create}
        fields={[{ name: "name", label: t("name") }, { name: "email", label: t("email") }, { name: "phone", label: t("phone") },
          { name: "family_id", label: t("family"), type: "select", options: famOptions }]} />
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("family")}</TableHead>
            <TableHead>{t("children")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("viewParent")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} data-testid="parent-dir-row">
                <TableCell className="font-medium text-slate-800">{p.name}
                  {p.is_primary && <Badge className="ml-2 bg-sky-100 text-sky-700 border-0">{t("primary")}</Badge>}
                  {p.is_payer && <Badge className="ml-1 bg-amber-100 text-amber-700 border-0">{t("is_payer")}</Badge>}
                </TableCell>
                <TableCell className="text-slate-500">{p.email || "—"}</TableCell>
                <TableCell className="text-slate-500">{p.family_name || "—"}</TableCell>
                <TableCell className="text-slate-500">{p.children_count}</TableCell>
                <TableCell className="text-slate-500">{p.account_status || p.status || "—"}</TableCell>
                <TableCell className="text-right">
                  <Link to={`/people/parents/${p.id}`} data-testid={`open-parent-${p.id}`}>
                    <Button size="sm" variant="outline" className="gap-2"><UserCog className="h-4 w-4" />{t("viewParent")}</Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
